import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useNavigate } from 'react-router-dom';

import { SidebarResponsive } from 'dashboard/components/sidebar/Sidebar';
import routes from 'pages/routes';
import { useAuth } from 'contexts/AuthContext';
import { useColorMode } from 'contexts/ColorModeContext';
import { API } from 'utilities/api';
import { timeAgo, initials as initialsOf } from 'utilities/format';
import AccountDialog from 'dashboard/components/account/AccountDialog';
import { ColorModeToggle } from 'dashboard/components/common';

const getDisplayName = (user) => {
  if (!user) return 'there';
  if (user.first_name) return user.first_name;
  if (user.username) return user.username;
  if (user.name) return user.name.split(' ')[0];
  if (user.email_address) return user.email_address.split('@')[0];
  return 'there';
};

export default function HeaderLinks(props) {
  const { selectedBrand } = props;
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { mode, toggle: toggleMode } = useColorMode();
  const router = routes(props).routes;
  router.shift();

  // Notification bell state — polled while a brand is selected.
  const [notifs, setNotifs] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [userAnchor, setUserAnchor] = useState(null);
  const [accountOpen, setAccountOpen] = useState(false);

  const loadNotifs = useCallback(() => {
    if (!selectedBrand) {
      setNotifs([]); setUnreadCount(0);
      return;
    }
    API.listNotifications({ brand_id: selectedBrand, limit: 8 })
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setNotifs(list);
        setUnreadCount(list.filter((n) => !n.read).length);
      })
      .catch(() => { setNotifs([]); setUnreadCount(0); });
  }, [selectedBrand]);

  useEffect(() => {
    loadNotifs();
    if (!selectedBrand) return undefined;
    const id = setInterval(loadNotifs, 60_000);
    return () => clearInterval(id);
  }, [loadNotifs, selectedBrand]);

  const markAllRead = (e) => {
    e?.stopPropagation();
    if (!selectedBrand) return;
    API.markAllNotificationsRead({ brand_id: selectedBrand })
      .then(() => loadNotifs())
      .catch(() => {});
  };

  const openNotification = (n) => {
    if (!n) return;
    setNotifAnchor(null);
    if (selectedBrand && !n.read) {
      API.markNotificationRead({ brand_id: selectedBrand, _id: n._id })
        .then(() => loadNotifs())
        .catch(() => {});
    }
    if (n.link) navigate(n.link);
  };

  const goNotificationsPage = () => {
    setNotifAnchor(null);
    navigate('/dashboard/brand/notifications');
  };

  const initials = initialsOf(
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : user?.name || user?.username || user?.email_address || user?.email || ''
  );
  const displayName = getDisplayName(user);

  const handleLogout = () => {
    try { logout && logout(); } catch (_) {}
    localStorage.clear();
    window.location.replace('/');
  };

  return (
    /**
     * Two containers on one line.
     *
     * The section you are in and what you can do to it is one thing; the
     * person signed in is another. Putting Publish next to the avatar reads
     * as though they belong together, and stacking them onto separate lines
     * wastes a row and pushes the page down. So: the same row, with visible
     * space between two surfaces that are plainly not the same surface.
     */
    <Stack direction='row' alignItems='center' spacing='8px' sx={{ minWidth: 0 }}>
      {/* The menu button on small screens, where the sidebar is a drawer. */}
      <SidebarResponsive routes={router} />

      {/* The account. Notifications and the avatar, and nothing of the
          page's — the brand picker lives at the top of the sidebar, where
          everything it scopes is listed beneath it. */}
      <Stack
        direction='row'
        alignItems='center'
        sx={{
          bgcolor: 'background.paper',
          p: '10px',
          borderRadius: '999px',
          // A shadow is absence of light, so on a dark page it cannot be a
          // pale blue-grey — that reads as a halo around the pill.
          boxShadow: (t) => (t.palette.mode === 'dark'
            ? '14px 17px 40px 4px rgba(0, 0, 0, 0.45)'
            : '14px 17px 40px 4px rgba(112, 144, 176, 0.18)'),
          flexShrink: 0,
        }}
      >
      <IconButton
        onClick={(e) => setNotifAnchor(e.currentTarget)}
        sx={{ position: 'relative', mx: '4px' }}
      >
        <NotificationsNoneIcon fontSize='small' />
        {unreadCount > 0 && (
          <Badge
            badgeContent={unreadCount > 9 ? '9+' : unreadCount}
            color='error'
            sx={{
              position: 'absolute', top: 8, right: 8,
              '& .MuiBadge-badge': { fontSize: '0.65em', height: 16, minWidth: 16 },
            }}
          />
        )}
      </IconButton>
      <Menu
        anchorEl={notifAnchor}
        open={Boolean(notifAnchor)}
        onClose={() => setNotifAnchor(null)}
        PaperProps={{
          sx: {
            mt: '22px',
            p: '20px',
            borderRadius: '20px',
            minWidth: { xs: 'unset', md: 400 },
            maxWidth: { xs: 360, md: 'unset' },
          },
        }}
      >
        <Stack direction='row' alignItems='center' mb='14px'>
          <Typography variant='body1' fontWeight='600'>Notifications</Typography>
          {unreadCount > 0 && (
            <Typography
              variant='body2'
              fontWeight='500'
              color='primary'
              sx={{ ml: 'auto', cursor: 'pointer' }}
              onClick={markAllRead}
            >
              Mark all read
            </Typography>
          )}
        </Stack>
        <Stack sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {!selectedBrand && (
            <Box px='0' mb='10px'>
              <Typography variant='body2' color='text.secondary'>
                Select a brand to see notifications.
              </Typography>
            </Box>
          )}
          {selectedBrand && notifs.length === 0 && (
            <Box px='0' mb='10px'>
              <Typography variant='body2' color='text.secondary'>
                You're all caught up.
              </Typography>
            </Box>
          )}
          {selectedBrand && notifs.map((n) => (
            <MenuItem
              key={n._id}
              onClick={() => openNotification(n)}
              sx={{
                borderRadius: '8px',
                px: '10px',
                py: '8px',
                mb: '6px',
                bgcolor: n.read ? 'transparent' : 'action.hover',
                whiteSpace: 'normal',
              }}
            >
              <Box width='100%'>
                <Stack direction='row' alignItems='center' justifyContent='space-between'>
                  <Typography variant='body2' fontWeight='600' noWrap>
                    {n.title}
                  </Typography>
                  <Typography variant='caption' color='text.secondary' whiteSpace='nowrap' ml='8px'>
                    {timeAgo(n.createdAt)}
                  </Typography>
                </Stack>
                {n.body && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    mt='2px'
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {n.body}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
        </Stack>
        {selectedBrand && (
          <Box mt='10px' pt='10px' borderTop='1px solid' borderColor='divider'>
            <Typography
              variant='body2'
              color='primary'
              fontWeight='600'
              textAlign='center'
              sx={{ cursor: 'pointer' }}
              onClick={goNotificationsPage}
            >
              View all
            </Typography>
          </Box>
        )}
      </Menu>

      {/* Light/dark, directly left of the avatar. It belongs in this pill
          rather than off on its own: it is a setting about you, like the
          account menu it sits against, and a floating button in the corner is
          a thing you find once and then hunt for every time after. */}
      <ColorModeToggle hideBelow='md' />

      <IconButton onClick={(e) => setUserAnchor(e.currentTarget)} sx={{ p: 0, ml: '4px' }}>
        <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: 14 }}>
          {initials}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={userAnchor}
        open={Boolean(userAnchor)}
        onClose={() => setUserAnchor(null)}
        PaperProps={{
          sx: {
            mt: '10px',
            borderRadius: '20px',
            minWidth: 220,
          },
        }}
      >
        <Box sx={{ px: '20px', pt: '16px', pb: '10px' }}>
          <Typography variant='body2' fontWeight='700'>
            Hi, {displayName}
          </Typography>
          {user?.email_address && (
            <Typography variant='caption' color='text.secondary'>
              {user.email_address}
            </Typography>
          )}
        </Box>
        <Divider />
        <Box p='10px'>
          {/* The two are separate objects and the menu now says so: "My
              account" is you, "Brand settings" is whichever brand is
              selected. Previously the only entry here was the brand's. */}
          {/* The light/dark switch, for the screens too narrow to carry it as
              an icon in the pill above. */}
          <MenuItem
            sx={{ borderRadius: '8px', px: '14px', display: { md: 'none' } }}
            onClick={() => { setUserAnchor(null); toggleMode(); }}
          >
            <Typography variant='body2'>
              {mode === 'dark' ? 'Light mode' : 'Dark mode'}
            </Typography>
          </MenuItem>
          <MenuItem
            sx={{ borderRadius: '8px', px: '14px' }}
            onClick={() => { setUserAnchor(null); setAccountOpen(true); }}
          >
            <Typography variant='body2'>Account settings</Typography>
          </MenuItem>
          <MenuItem
            sx={{ borderRadius: '8px', px: '14px', color: 'error.main' }}
            onClick={handleLogout}
          >
            <Typography variant='body2'>Log out</Typography>
          </MenuItem>
        </Box>
      </Menu>
      </Stack>

      <AccountDialog open={accountOpen} onClose={() => setAccountOpen(false)} />
    </Stack>
  );
}
